import { useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGenerationStore } from '@/stores/generation';
import {
  Wand2,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ListChecks,
  Image,
  FileText,
  ArrowLeft,
  ChevronRight,
  Zap,
  HourglassIcon,
  List,
  AlertCircle,
  Timer,
  User,
  Layers,
  SkipForward,
  Pause,
  Code,
  AlertTriangle,
  ExternalLink,
  Archive,
  ArchiveRestore,
  Eye,
  EyeOff,
} from 'lucide-react';
import { clsx } from 'clsx';
import type {
  ActiveWorkflowRun,
  GenerationRun,
  StepLogEntry,
  RunDetailResponse,
} from '@/services/api';

// ---------------------------------------------------------------------------
// Types for view state (now derived from URL)
// ---------------------------------------------------------------------------

type View =
  | { kind: 'dashboard' }
  | { kind: 'detail'; runId: string }
  | { kind: 'playbooks' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function elapsed(seconds?: number): string {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function pipelineLabel(pipeline?: string): string {
  const map: Record<string, string> = {
    ad_generation: 'Ad Pack Pipeline',
    asset_pipeline: 'Asset Pipeline',
    creative_workflow: 'Creative Workflow',
  };
  return map[pipeline ?? ''] ?? pipeline ?? 'Generation Run';
}

function runTypeLabel(runType?: string): string {
  const map: Record<string, string> = {
    full_asset_pack: 'Full Asset Pack',
    full_adpack: 'Full Ad Pack',
    angles_only: 'Angles',
    scripts_only: 'Scripts',
    video_prompts: 'Video Prompts',
    video_generation: 'Video Generation',
    image_generation: 'Image Generation',
    static_ad_campaign: 'Static Ad Campaign',
    ad_brief_generation: 'Ad Brief',
    creative_review_batch: 'Creative Review',
  };
  return map[runType ?? ''] ?? runType ?? '-';
}

function stepTitle(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/20',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  queued: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  paused: 'bg-purple-50 text-purple-700 border-purple-200',
  skipped: 'bg-slate-50 text-slate-400 border-slate-200',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function StatusIcon({ status, size = 14 }: { status: string; size?: number }) {
  const map: Record<string, React.ElementType> = {
    completed: CheckCircle2,
    failed: XCircle,
    running: Loader2,
    pending: Clock,
    queued: HourglassIcon,
    skipped: SkipForward,
    cancelled: XCircle,
    paused: Pause,
  };
  const Icon = map[status] ?? Clock;
  return (
    <Icon
      size={size}
      className={clsx(status === 'running' && 'animate-spin')}
    />
  );
}

function ProgressBar({
  percent,
  failed,
  className,
}: {
  percent: number;
  failed?: boolean;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'bg-slate-100 rounded-full overflow-hidden',
        className ?? 'h-2',
      )}
    >
      <div
        className={clsx(
          'h-full rounded-full transition-all duration-500',
          failed ? 'bg-rose-400' : 'bg-brand-teal',
        )}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page - URL-routed for browser history support
// ---------------------------------------------------------------------------

export function GenerationPage() {
  const { runId } = useParams<{ runId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive view from URL path
  const getView = (): View => {
    if (location.pathname.endsWith('/playbooks')) return { kind: 'playbooks' };
    if (runId) return { kind: 'detail', runId };
    return { kind: 'dashboard' };
  };

  const view = getView();

  const handleOpenRun = (id: string) => navigate(`/generation/${id}`);
  const handleOpenPlaybooks = () => navigate('/generation/playbooks');
  const handleBack = () => navigate('/generation');

  return (
    <div className="space-y-8">
      {view.kind === 'dashboard' && (
        <DashboardView
          onOpenRun={handleOpenRun}
          onOpenPlaybooks={handleOpenPlaybooks}
        />
      )}
      {view.kind === 'detail' && (
        <DetailView
          runId={view.runId}
          onBack={handleBack}
        />
      )}
      {view.kind === 'playbooks' && (
        <PlaybooksView onBack={handleBack} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard View
// ---------------------------------------------------------------------------

function DashboardView({
  onOpenRun,
  onOpenPlaybooks,
}: {
  onOpenRun: (id: string) => void;
  onOpenPlaybooks: () => void;
}) {
  const navigate = useNavigate();
  const {
    workflows,
    queue,
    runs,
    loading,
    error,
    fetchDashboard,
    showArchived,
    setShowArchived,
    archiveRun,
    unarchiveRun,
    archiving,
  } = useGenerationStore();

  useEffect(() => {
    fetchDashboard();
    const id = setInterval(fetchDashboard, 15_000);
    return () => clearInterval(id);
  }, [fetchDashboard]);

  const activeRuns = workflows?.active_runs ?? [];
  const queuedRuns = workflows?.queued_runs ?? [];

  return (
    <>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-900">
          Generation Workflows
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Track and inspect multi-step creative generation pipelines.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Queue Stats */}
      {queue && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Play}
            label="Active Runs"
            value={queue.active_runs}
            accent
          />
          <StatCard
            icon={Clock}
            label="Pending Tasks"
            value={queue.pending_tasks}
          />
          <StatCard
            icon={Loader2}
            label="Running Tasks"
            value={queue.running_tasks}
          />
          <StatCard
            icon={CheckCircle2}
            label="Completed Today"
            value={queue.completed_today ?? 0}
          />
        </div>
      )}

      {/* Active Workflows */}
      {activeRuns.length > 0 && (
        <Section icon={Zap} title="Active Workflows" count={activeRuns.length}>
          <div className="space-y-3">
            {activeRuns.map((r) => (
              <RunCard key={r.run_id} run={r} onClick={() => onOpenRun(r.run_id)} />
            ))}
          </div>
        </Section>
      )}

      {/* Queued */}
      {queuedRuns.length > 0 && (
        <Section icon={HourglassIcon} title="Queued" count={queuedRuns.length}>
          <div className="space-y-3">
            {queuedRuns.map((r) => (
              <RunCard key={r.run_id} run={r} onClick={() => onOpenRun(r.run_id)} />
            ))}
          </div>
        </Section>
      )}

      {/* All Runs */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SectionHeader icon={List} title="All Runs" subtitle={`${runs.length} total`} />
          <div className="flex gap-2">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={clsx(
                'text-xs font-medium px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5',
                showArchived
                  ? 'text-brand-purple bg-brand-purple/10 border-brand-purple/30'
                  : 'text-slate-500 hover:text-brand-teal border-slate-200 hover:border-brand-teal/30'
              )}
            >
              {showArchived ? <EyeOff size={12} /> : <Eye size={12} />}
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
            <button
              onClick={() => navigate('/briefs')}
              className="text-xs font-medium text-slate-500 hover:text-brand-teal px-3 py-1.5 rounded-lg border border-slate-200 hover:border-brand-teal/30 transition-all"
            >
              <FileText size={12} className="inline mr-1" />
              Briefs
            </button>
            <button
              onClick={onOpenPlaybooks}
              className="text-xs font-medium text-slate-500 hover:text-brand-teal px-3 py-1.5 rounded-lg border border-slate-200 hover:border-brand-teal/30 transition-all"
            >
              <Layers size={12} className="inline mr-1" />
              Playbooks
            </button>
          </div>
        </div>

        {runs.length > 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Run</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Progress</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.map((r) => (
                  <RunRow
                    key={r.run_id}
                    run={r}
                    onClick={() => onOpenRun(r.run_id)}
                    onArchive={() => archiveRun(r.run_id)}
                    onUnarchive={() => unarchiveRun(r.run_id)}
                    isArchiving={archiving === r.run_id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
            <Wand2 size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No generation runs yet</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Runs will appear here when triggered via the ad generation pipeline
              or API.
            </p>
          </div>
        )}
      </div>

      {loading && (
        <p className="text-[10px] text-slate-400 font-mono text-center">
          refreshing...
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail View
// ---------------------------------------------------------------------------

function DetailView({
  runId,
  onBack,
}: {
  runId: string;
  onBack: () => void;
}) {
  const { detail, loading, error, fetchDetail, archiveRun, unarchiveRun, archiving, subscribeToDetail, unsubscribeDetail, isRealtime } = useGenerationStore();

  // Subscribe to real-time updates via Firestore
  useEffect(() => {
    subscribeToDetail(runId);
    return () => unsubscribeDetail();
  }, [runId, subscribeToDetail, unsubscribeDetail]);

  const handleArchive = useCallback(() => {
    if (detail?.run?.archived) {
      unarchiveRun(runId);
    } else {
      archiveRun(runId);
    }
  }, [detail?.run?.archived, runId, archiveRun, unarchiveRun]);

  if (loading && !detail) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center shadow-sm">
        <Loader2 size={24} className="animate-spin text-brand-teal mb-3" />
        <p className="text-sm text-slate-500">Loading workflow detail...</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <>
        <BackButton onClick={onBack} />
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error ?? `Run ${runId} not found`}</span>
        </div>
      </>
    );
  }

  const { run, execution_log: log, tasks, assets, content } = detail;
  const status = run.status ?? 'unknown';
  const pct = Math.round(run.progress?.percent_complete ?? 0);
  const steps = log?.steps ?? [];

  return (
    <>
      <BackButton onClick={onBack} />

      {/* Run Header Card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center text-brand-teal">
              <Wand2 size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-navy-900">
                {pipelineLabel(run.source_pipeline)}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                <span className="font-mono">{run.run_id}</span>
                {run.source_job_id && (
                  <span>
                    · Job:{' '}
                    <span className="text-brand-purple">
                      {run.source_job_id}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Real-time indicator */}
            {isRealtime && (
              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Live
              </span>
            )}
            {/* Archive button */}
            <button
              onClick={handleArchive}
              disabled={archiving === runId}
              className={clsx(
                'p-2 rounded-lg border transition-all',
                archiving === runId
                  ? 'opacity-50 cursor-not-allowed'
                  : run.archived
                    ? 'text-emerald-600 hover:bg-emerald-50 border-emerald-200 hover:border-emerald-300'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
              )}
              title={run.archived ? 'Unarchive run' : 'Archive run'}
            >
              {archiving === runId ? (
                <Loader2 size={14} className="animate-spin" />
              ) : run.archived ? (
                <ArchiveRestore size={14} />
              ) : (
                <Archive size={14} />
              )}
            </button>
            {/* Status badge */}
            <span
              className={clsx(
                'text-xs font-bold uppercase px-3 py-1.5 rounded-full border flex items-center gap-1.5',
                STATUS_COLORS[status] ?? STATUS_COLORS.pending,
              )}
            >
              <StatusIcon status={status} size={12} />
              {status}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-4 mb-4">
          <ProgressBar
            percent={pct}
            failed={status === 'failed'}
            className="flex-1 h-3"
          />
          <span className="text-lg font-bold text-navy-900">{pct}%</span>
        </div>

        {/* Run metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <MetaItem label="Client" value={run.client_uid || '-'} />
          <MetaItem label="Run Type" value={runTypeLabel(run.run_type)} />
          <MetaItem
            label="Started"
            value={
              run.started_at
                ? new Date(run.started_at).toLocaleString()
                : '-'
            }
          />
          <MetaItem
            label="Elapsed"
            value={elapsed(run.progress?.elapsed_seconds)}
          />
        </div>
      </div>

      {/* Execution Steps Timeline */}
      <Section
        icon={ListChecks}
        title="Execution Steps"
        subtitle={`${log?.steps_completed ?? 0}/${log?.total_steps ?? steps.length} completed`}
        iconBg="bg-brand-purple/10 text-brand-purple border-brand-purple/20"
      >
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          {steps.length > 0 ? (
            <div className="space-y-1">
              {steps.map((step, i) => (
                <StepRow
                  key={step.step_name}
                  step={step}
                  isLast={i === steps.length - 1}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-6 text-sm">
              No execution steps recorded
            </p>
          )}
        </div>
      </Section>

      {/* Tasks */}
      {tasks.length > 0 && (
        <Section icon={ListChecks} title="Tasks" count={tasks.length} iconBg="bg-blue-50 text-blue-500 border-blue-100">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-bold text-slate-500 uppercase">Task</th>
                  <th className="text-left px-4 py-2 font-bold text-slate-500 uppercase">Type</th>
                  <th className="text-left px-4 py-2 font-bold text-slate-500 uppercase">Platform</th>
                  <th className="text-left px-4 py-2 font-bold text-slate-500 uppercase">Status</th>
                  <th className="text-left px-4 py-2 font-bold text-slate-500 uppercase">Prompt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tasks.map((t) => (
                  <tr key={t.task_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-[10px] text-brand-purple">
                      {t.task_id}
                    </td>
                    <td className="px-4 py-2">{t.task_type}</td>
                    <td className="px-4 py-2">{t.platform}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="px-4 py-2 text-slate-500 max-w-xs truncate">
                      {t.prompt || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Assets */}
      {assets.length > 0 && (
        <Section icon={Image} title="Generated Assets" count={assets.length} iconBg="bg-emerald-50 text-emerald-500 border-emerald-100">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {assets.map((a) => (
              <div
                key={a.asset_id}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Image size={12} className="text-brand-purple" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase">
                    {a.asset_type}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-slate-400 truncate">
                  {a.asset_id}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {a.format} · {a.platform}
                </div>
                {a.storage?.url && (
                  <a
                    href={a.storage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-brand-teal hover:underline mt-2 flex items-center gap-1"
                  >
                    <ExternalLink size={10} />
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Content */}
      {content.length > 0 && (
        <Section icon={FileText} title="Generated Content" count={content.length} iconBg="bg-amber-50 text-amber-500 border-amber-100">
          <div className="space-y-3">
            {content.map((c) => (
              <div
                key={c.content_id}
                className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={12} className="text-warning" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase">
                    {c.content_type}
                  </span>
                  <span className="font-mono text-[10px] text-slate-300 ml-auto">
                    {c.content_id}
                  </span>
                </div>
                <pre className="text-[11px] text-slate-600 bg-slate-50 p-3 rounded-lg overflow-x-auto max-h-32 border border-slate-100">
                  {JSON.stringify(c.content, null, 2)?.substring(0, 500)}
                </pre>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Run Metadata */}
      {run.metadata && Object.keys(run.metadata).length > 0 && (
        <Section icon={Code} title="Run Metadata" iconBg="bg-slate-100 text-slate-500 border-slate-200">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <pre className="text-[11px] text-slate-600 overflow-x-auto">
              {JSON.stringify(run.metadata, null, 2)}
            </pre>
          </div>
        </Section>
      )}

      {/* Errors */}
      {(run.errors?.length ?? 0) > 0 && (
        <Section icon={AlertTriangle} title="Errors" iconBg="bg-rose-50 text-rose-500 border-rose-100">
          <div className="space-y-2">
            {run.errors!.map((e, i) => (
              <div
                key={i}
                className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700"
              >
                <span className="font-bold">{e.step ?? 'unknown'}:</span>{' '}
                {e.error ?? JSON.stringify(e)}
                {e.at && (
                  <span className="text-rose-400 ml-2">{timeAgo(e.at)}</span>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Playbooks View
// ---------------------------------------------------------------------------

function PlaybooksView({ onBack }: { onBack: () => void }) {
  const { playbooks, playbooksTotal, loading, error, fetchPlaybooks } =
    useGenerationStore();

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  return (
    <>
      <BackButton onClick={onBack} />
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-bold text-navy-900">Playbooks</h1>
        <span className="text-xs text-slate-400">{playbooksTotal} total</span>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && playbooks.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 flex items-center justify-center shadow-sm">
          <Loader2 size={24} className="animate-spin text-brand-teal" />
        </div>
      ) : playbooks.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Playbook ID</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Assets</th>
                <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {playbooks.map((p) => (
                <tr key={p.playbook_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-[10px] text-brand-purple">
                    {p.playbook_id}
                  </td>
                  <td className="px-4 py-3 font-medium text-navy-900">
                    {p.name}
                  </td>
                  <td className="px-4 py-3">{p.client_uid}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-3">
                    {p.asset_summary?.total_assets ?? 0}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {timeAgo(p.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm text-slate-400">
          No playbooks found
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent ? 'text-brand-teal' : 'text-slate-400'} />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-2xl font-bold text-navy-900">{value}</div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  count,
  subtitle,
  iconBg,
  children,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
  subtitle?: string;
  iconBg?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <SectionHeader icon={Icon} title={title} count={count} subtitle={subtitle} iconBg={iconBg} />
      {children}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  count,
  subtitle,
  iconBg,
}: {
  icon: React.ElementType;
  title: string;
  count?: number;
  subtitle?: string;
  iconBg?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={clsx(
          'w-8 h-8 rounded-lg flex items-center justify-center border',
          iconBg ?? 'bg-brand-teal/10 text-brand-teal border-brand-teal/20',
        )}
      >
        <Icon size={14} />
      </div>
      <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">
        {title}
      </h2>
      {count !== undefined && (
        <span className="text-[10px] font-mono bg-brand-teal/10 text-brand-teal px-2 py-0.5 rounded-full border border-brand-teal/20">
          {count}
        </span>
      )}
      {subtitle && (
        <span className="text-[10px] text-slate-400 font-mono">{subtitle}</span>
      )}
    </div>
  );
}

function RunCard({
  run: r,
  onClick,
}: {
  run: ActiveWorkflowRun;
  onClick: () => void;
}) {
  const pct = Math.round(r.percent_complete ?? 0);
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:border-brand-teal/30 hover:shadow-soft transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal border border-brand-teal/20">
            <Wand2 size={16} />
          </div>
          <div>
            <div className="font-bold text-sm text-navy-900">
              {pipelineLabel(r.source_pipeline)}
            </div>
            <div className="text-[11px] text-slate-400 font-mono">
              {r.run_id}
            </div>
          </div>
        </div>
        <StatusBadge status={r.status} />
      </div>
      <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <User size={10} /> {r.client_uid || '-'}
        </span>
        <span className="flex items-center gap-1">
          <Layers size={10} /> {runTypeLabel(r.run_type)}
        </span>
        {r.current_step && (
          <span className="flex items-center gap-1">
            <ChevronRight size={10} /> {r.current_step}
          </span>
        )}
        {r.elapsed_seconds && (
          <span className="flex items-center gap-1">
            <Timer size={10} /> {elapsed(r.elapsed_seconds)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <ProgressBar
          percent={pct}
          failed={r.status === 'failed'}
          className="flex-1 h-2"
        />
        <span className="text-[11px] font-bold text-navy-900 min-w-[3rem] text-right">
          {pct}%
        </span>
        <span className="text-[10px] text-slate-400">
          {r.steps_completed ?? 0}/{r.total_steps ?? 0} steps
        </span>
      </div>
    </div>
  );
}

function RunRow({
  run: r,
  onClick,
  onArchive,
  onUnarchive,
  isArchiving,
}: {
  run: GenerationRun;
  onClick: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  isArchiving?: boolean;
}) {
  const pct = Math.round(r.progress?.percent_complete ?? 0);
  const status = r.status ?? 'unknown';
  const isArchived = r.archived ?? false;

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isArchived) {
      onUnarchive?.();
    } else {
      onArchive?.();
    }
  };

  return (
    <tr
      className={clsx(
        'hover:bg-slate-50 cursor-pointer transition-colors',
        isArchived && 'opacity-60'
      )}
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="font-mono text-[11px] text-brand-purple">
            {r.run_id}
          </div>
          {isArchived && (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">
              Archived
            </span>
          )}
        </div>
        {r.source_pipeline && (
          <div className="text-[10px] text-slate-400">
            {pipelineLabel(r.source_pipeline)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600">{runTypeLabel(r.run_type)}</td>
      <td className="px-4 py-3 font-medium text-navy-900">
        {r.client_uid || '-'}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={status} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <ProgressBar
            percent={pct}
            failed={status === 'failed'}
            className="w-16 h-1.5"
          />
          <span className="text-[10px] font-bold text-slate-500">{pct}%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-slate-400">{timeAgo(r.created_at)}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={handleArchiveClick}
            disabled={isArchiving}
            className={clsx(
              'p-1.5 rounded-lg border transition-all',
              isArchiving
                ? 'opacity-50 cursor-not-allowed'
                : isArchived
                  ? 'text-emerald-600 hover:bg-emerald-50 border-emerald-200 hover:border-emerald-300'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
            )}
            title={isArchived ? 'Unarchive run' : 'Archive run'}
          >
            {isArchiving ? (
              <Loader2 size={12} className="animate-spin" />
            ) : isArchived ? (
              <ArchiveRestore size={12} />
            ) : (
              <Archive size={12} />
            )}
          </button>
          <ChevronRight size={10} className="text-slate-300" />
        </div>
      </td>
    </tr>
  );
}

function StepRow({
  step,
  isLast,
}: {
  step: StepLogEntry;
  isLast: boolean;
}) {
  const status = step.status ?? 'pending';
  const iconBg: Record<string, string> = {
    completed: 'bg-emerald-500 text-white',
    running: 'bg-brand-teal text-white',
    failed: 'bg-rose-500 text-white',
    skipped: 'bg-slate-200 text-slate-400',
    pending: 'bg-slate-100 text-slate-400',
  };

  return (
    <div className="flex items-start gap-4 pb-4 relative">
      {/* Connecting line */}
      {!isLast && (
        <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-slate-200" />
      )}
      {/* Icon */}
      <div
        className={clsx(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10',
          iconBg[status] ?? iconBg.pending,
        )}
      >
        <StatusIcon status={status} size={12} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-navy-900">
              {stepTitle(step.step_name)}
            </span>
            <StatusBadge status={status} />
          </div>
          {step.duration_seconds != null && (
            <span className="text-[10px] text-slate-400 font-mono">
              {elapsed(step.duration_seconds)}
            </span>
          )}
        </div>
        {step.message && (
          <p className="text-xs text-slate-500 mt-0.5">{step.message}</p>
        )}
        {step.items_total > 0 && (
          <div className="flex items-center gap-2 mt-1">
            <ProgressBar
              percent={Math.round(
                (step.items_completed / Math.max(step.items_total, 1)) * 100,
              )}
              className="w-24 h-1.5"
            />
            <span className="text-[10px] text-slate-400">
              {step.items_completed}/{step.items_total}
              {step.items_failed > 0 && ` (${step.items_failed} failed)`}
            </span>
          </div>
        )}
        {step.error && (
          <div className="text-[11px] text-rose-600 bg-rose-50 px-2 py-1 rounded mt-1 border border-rose-100">
            {step.error}
          </div>
        )}
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-xs text-slate-400 hover:text-brand-teal transition-colors flex items-center gap-1"
    >
      <ArrowLeft size={12} />
      Back to Workflows
    </button>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[10px] text-slate-400 uppercase font-bold">
        {label}
      </span>
      <div className="font-medium text-navy-900 mt-0.5">{value}</div>
    </div>
  );
}
